import { Alert, Checkbox, Form, Input, Modal, message } from "antd";
import { createInternalCustomer } from "api/coreapi/customers";
import IdentificationNumbersSelect from "components/identificationNumbersSelect";
import ErrorMessage from "components/errorMessage";
import { useEffect, useState } from "react";
import { CustomerOverview } from "shared/models/customerOverviewModel";
import { CreateInternalCustomerPayload } from "shared/models/payloads/createInternalCustomerPayload";

interface QuickCreateInternalCustomerModalProps {
    open: boolean;
    onCancel: () => void;
    onCreated: (customer: CustomerOverview) => void;
}

const QuickCreateInternalCustomerModal = ({ open, onCancel, onCreated }: QuickCreateInternalCustomerModalProps) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [conflictMessage, setConflictMessage] = useState<string>();
    const includeContact = Form.useWatch("includeContact", form);

    useEffect(() => {
        if (!open) {
            form.resetFields();
            setConflictMessage(undefined);
        }
    }, [form, open]);

    const handleSubmit = async (values: any) => {
        const payload: CreateInternalCustomerPayload = {
            name: values.name.trim(),
            identificationNumberTypeId: values.identificationNumberTypeId,
            identificationNumber: values.identificationNumber.trim(),
            contact: values.includeContact ? {
                firstName: values.contact.firstName.trim(),
                lastName: values.contact.lastName?.trim() || undefined,
                jobTitle: values.contact.jobTitle?.trim() || undefined,
                phoneNumber: values.contact.phoneNumber?.trim() || undefined,
                emailAddress: values.contact.emailAddress?.trim() || undefined,
            } : undefined,
        };

        setLoading(true);
        setConflictMessage(undefined);
        try {
            const response = await createInternalCustomer(payload);
            if (response.result) {
                message.success("Cliente creado correctamente.");
                onCreated(response.result);
            }
        }
        catch (error: any) {
            const errors = Array.isArray(error.errors) ? error.errors : ["No fue posible crear el cliente."];
            if (error.statusCode === 409) {
                setConflictMessage(errors.join(" "));
            }
            else {
                message.error(ErrorMessage(errors));
            }
        }
        finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Crear cliente rápido"
            open={open}
            okText="Crear cliente"
            cancelText="Cancelar"
            confirmLoading={loading}
            onOk={() => form.submit()}
            onCancel={onCancel}
            destroyOnClose>
            {conflictMessage && (
                <Alert
                    type="warning"
                    showIcon
                    message="El cliente ya existe"
                    description={`${conflictMessage} Puedes cerrar este diálogo y buscar el cliente existente.`}
                    style={{ marginBottom: 16 }} />
            )}
            <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ includeContact: false }}>
                <Form.Item label="Nombre" name="name" rules={[{ required: true, whitespace: true }, { max: 500 }]}>
                    <Input maxLength={500} />
                </Form.Item>
                <Form.Item label="Tipo de identificación" name="identificationNumberTypeId" rules={[{ required: true }]}>
                    <IdentificationNumbersSelect />
                </Form.Item>
                <Form.Item label="Número de identificación" name="identificationNumber" rules={[{ required: true, whitespace: true }, { max: 50 }]}>
                    <Input maxLength={50} />
                </Form.Item>
                <Form.Item name="includeContact" valuePropName="checked">
                    <Checkbox>Incluir un contacto</Checkbox>
                </Form.Item>
                {includeContact && (
                    <>
                        <Form.Item label="Nombre del contacto" name={["contact", "firstName"]} rules={[{ required: true, whitespace: true }, { max: 200 }]}>
                            <Input maxLength={200} />
                        </Form.Item>
                        <Form.Item label="Apellido" name={["contact", "lastName"]} rules={[{ max: 200 }]}>
                            <Input maxLength={200} />
                        </Form.Item>
                        <Form.Item label="Cargo" name={["contact", "jobTitle"]} rules={[{ max: 200 }]}>
                            <Input maxLength={200} />
                        </Form.Item>
                        <Form.Item label="Teléfono" name={["contact", "phoneNumber"]} rules={[{ max: 200 }]}>
                            <Input maxLength={200} />
                        </Form.Item>
                        <Form.Item label="Correo electrónico" name={["contact", "emailAddress"]} rules={[{ type: "email" }, { max: 200 }]}>
                            <Input maxLength={200} />
                        </Form.Item>
                    </>
                )}
            </Form>
        </Modal>
    );
};

export default QuickCreateInternalCustomerModal;
